import {
  Text,
  Stack,
  Group,
  Badge,
  Box,
  Alert,
} from '@mantine/core';
import { Info } from 'react-feather';
import type { ReviewStepProps } from './types';
import type { ModalityType } from '../../../types';
import { useFeatureFlag } from '../../../stores/featureFlags';
import {
  MODALITY_LABELS,
  FREQUENCY_LABELS,
  WORKSHOP_PATTERN_LABELS,
  COACHING_SUB_TYPE_LABELS,
  LIVE_SESSION_DELIVERY_LABEL,
} from '../../../config/cohortConfig';
import { reviewTimeSlotLines } from './timeSlotCreation';
import classes from './CreateCohortPage.module.css';

export function ReviewStep({
  form,
  selectedPartner,
  selectedProgram,
  partnerContacts,
  internalUsers,
  jobTitleGroups,
}: ReviewStepProps) {
  const {
    selectedModalities,
    groupCoachingConfig,
    oneOnOneConfig,
    workshopConfig,
    sessionDates,
    checkins,
  } = form.values;

  // MED-1350: the check-ins line sits under the 1:1 entry, or under Group
  // Coaching when there is no 1:1 coaching.
  const checkinHost: ModalityType | null = selectedModalities.includes('one_on_one')
    ? 'one_on_one'
    : selectedModalities.includes('group_coaching')
      ? 'group_coaching'
      : null;
  const checkinWeekText = (week: number | null) => (week !== null ? `week ${week}` : 'week not set');
  const checkinParts = [
    checkins.introEnabled ? `Intro ${checkinWeekText(checkins.introWeek)}` : null,
    checkins.midpointEnabled ? `Midpoint ${checkinWeekText(checkins.midpointWeek)}` : null,
  ].filter((part): part is string => part !== null);
  const checkinSummary = (
    <Text size="xs" c="dimmed">
      Check-ins: {checkinParts.length > 0 ? checkinParts.join(', ') : 'Off'}
    </Text>
  );

  // MED-948 (E1.10a) AC6/AC11 — the Live Session Delivery line only appears when
  // the system-level flag is on, matching the gated setup step.
  const liveSessionDeliveryFlag = useFeatureFlag('live_session_delivery');

  // Get selected contact names
  const selectedContacts = form.values.primaryContactIds
    .map(id => partnerContacts.find(c => c.id === id))
    .filter(Boolean)
    .map(c => `${c!.first_name} ${c!.last_name}`);

  // Count scheduled dates per modality
  const countScheduledDates = (modality: ModalityType) => {
    const dates = sessionDates[modality] || [];
    return dates.filter(d => d !== null).length;
  };

  // Get total session count for a modality
  const getSessionCount = (modality: ModalityType) => {
    switch (modality) {
      case 'group_coaching':
        return groupCoachingConfig.sessionCount;
      case 'one_on_one':
        return oneOnOneConfig.sessionCount;
      case 'workshop':
        return workshopConfig.workshops.length;
    }
  };

  // Get user name by ID
  const getUserName = (userId: string) => {
    const user = internalUsers.find(u => u.id === userId);
    return user?.name || 'Unassigned';
  };

  // MED-1268: the time slots, in the order they save, with their slot numbers.
  const timeSlotLines = reviewTimeSlotLines(form.values.timeSlots || []);

  // Check if any team assignments exist
  const hasTeamAssignments = Object.values(form.values.teamAssignments).some(v => v);

  return (
    <Stack gap="lg">
      <Text size="lg" fw={600}>Review & Create</Text>

      {/* Partner & Program */}
      <Box className={classes.reviewSection}>
        <Text size="xs" fw={600} c="dimmed" mb="sm" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
          Partner & Program
        </Text>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm">Partner:</Text>
            <Text size="sm" fw={500}>{selectedPartner?.name || 'Not selected'}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm">Program:</Text>
            <Text size="sm" fw={500}>
              {selectedProgram?.name || form.values.newProgramName || 'Not selected'}
            </Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm">Primary Contacts:</Text>
            <Text size="sm" fw={500}>
              {selectedContacts.length > 0 ? selectedContacts.join(', ') : 'None selected'}
            </Text>
          </Group>
        </Stack>
      </Box>

      {/* Cohort Details */}
      <Box className={classes.reviewSection}>
        <Text size="xs" fw={600} c="dimmed" mb="sm" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
          Cohort Details
        </Text>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm">Name:</Text>
            <Text size="sm" fw={500}>{form.values.name || 'Not set'}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm">Short Name:</Text>
            <Text size="sm" fw={500}>{form.values.shortName || 'Not set'}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm">Code:</Text>
            {form.values.code ? (
              <Badge color="gray" variant="light">{form.values.code}</Badge>
            ) : (
              <Text size="sm" fw={500}>Not set</Text>
            )}
          </Group>
        </Stack>
      </Box>

      {/* Team Assignments */}
      <Box className={classes.reviewSection}>
        <Text size="xs" fw={600} c="dimmed" mb="sm" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
          Team Assignments
        </Text>
        {hasTeamAssignments ? (
          <Stack gap="md">
            {jobTitleGroups.map((group) => {
              const assignedTitles = group.titles.filter(
                title => form.values.teamAssignments[title.id]
              );
              if (assignedTitles.length === 0) return null;
              return (
                <Box key={group.category}>
                  <Text size="xs" fw={600} c="dimmed" mb="xs">{group.category}</Text>
                  <Stack gap="xs">
                    {assignedTitles.map((title) => (
                      <Group key={title.id} justify="space-between">
                        <Text size="sm">{title.name}:</Text>
                        <Text size="sm" fw={500}>
                          {getUserName(form.values.teamAssignments[title.id])}
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">No team assignments configured</Text>
        )}
      </Box>

      {/* Modules & Sessions */}
      <Box className={classes.reviewSection}>
        <Text size="xs" fw={600} c="dimmed" mb="sm" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
          Modules & Sessions
        </Text>
        {selectedModalities.length > 0 ? (
          <Stack gap="md">
            {selectedModalities.map((modality) => (
              <Box key={modality}>
                <Text size="sm" fw={500} mb="xs">{MODALITY_LABELS[modality]}</Text>

                {modality === 'group_coaching' && (
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">
                      {groupCoachingConfig.sessionCount} sessions, {FREQUENCY_LABELS[groupCoachingConfig.frequency]}
                    </Text>
                    {/* MED-848 AC7 — display sub-type for group coaching */}
                    <Text size="xs" c="dimmed">
                      Sub-type:{' '}
                      {groupCoachingConfig.coachingSubType
                        ? COACHING_SUB_TYPE_LABELS[groupCoachingConfig.coachingSubType]
                        : 'Not set'}
                    </Text>
                    {/* MED-948 (E1.10a) AC6 — display Live Session Delivery status
                        for group coaching (only when the system flag is on). */}
                    {liveSessionDeliveryFlag && (
                      <Text size="xs" c="dimmed">
                        {LIVE_SESSION_DELIVERY_LABEL}:{' '}
                        {groupCoachingConfig.liveSessionDeliveryEnabled ? 'Enabled' : 'Disabled'}
                      </Text>
                    )}
                    {/* MED-848 AC7 — AI features status. Only meaningful (and only
                        configurable) when the Live Session Delivery gate is on
                        (MED-948 AC4), so mirror that gating here. */}
                    {liveSessionDeliveryFlag && groupCoachingConfig.liveSessionDeliveryEnabled && (
                      <Text size="xs" c="dimmed">
                        AI features: {groupCoachingConfig.aiFeaturesEnabled ? 'Enabled' : 'Disabled'}
                      </Text>
                    )}
                    {/* MED-1069 (E1.10c) AC8 — Commitment Tracking status. Gated by
                        the system flag like the other lines, but shown regardless
                        of the Live Session Delivery toggle (independent, AC7). */}
                    {liveSessionDeliveryFlag && (
                      <Text size="xs" c="dimmed">
                        Commitment Tracking:{' '}
                        {groupCoachingConfig.commitmentTrackingEnabled ? 'Enabled' : 'Disabled'}
                      </Text>
                    )}
                    {checkinHost === 'group_coaching' && checkinSummary}
                  </Stack>
                )}

                {modality === 'one_on_one' && (
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">
                      {oneOnOneConfig.sessionCount} sessions, {FREQUENCY_LABELS[oneOnOneConfig.frequency]}
                    </Text>
                    {checkinHost === 'one_on_one' && checkinSummary}
                  </Stack>
                )}

                {modality === 'workshop' && (
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">
                      {workshopConfig.workshops.length} workshop{workshopConfig.workshops.length !== 1 ? 's' : ''}, {WORKSHOP_PATTERN_LABELS[workshopConfig.pattern]}
                    </Text>
                    {workshopConfig.workshops.length > 0 && (
                      <Text size="xs" c="dimmed">
                        Workshops: {workshopConfig.workshops.map(w => w.title || 'Untitled').join(', ')}
                      </Text>
                    )}
                    {/* MED-848 AC7 — display AI features status for workshop */}
                    <Text size="xs" c="dimmed">
                      AI features: {workshopConfig.aiFeaturesEnabled ? 'Enabled' : 'Disabled'}
                    </Text>
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">No modalities selected</Text>
        )}
      </Box>

      {/* Scheduling */}
      <Box className={classes.reviewSection}>
        <Text size="xs" fw={600} c="dimmed" mb="sm" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
          Scheduling
        </Text>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm">Date Constraints:</Text>
            <Text size="sm" fw={500}>
              {form.values.dateConstraints.length} constraint{form.values.dateConstraints.length !== 1 ? 's' : ''}
            </Text>
          </Group>

          {/* Session dates per modality */}
          {selectedModalities.map((modality) => (
            <Group key={modality} justify="space-between">
              <Text size="sm">{MODALITY_LABELS[modality]} Dates:</Text>
              <Text size="sm" fw={500}>
                {countScheduledDates(modality)}/{getSessionCount(modality)} scheduled
              </Text>
            </Group>
          ))}

          {/* MED-1268: the time slots, listed like the other Review lines. */}
          <Group justify="space-between">
            <Text size="sm">Time Slots:</Text>
            <Text size="sm" fw={500}>
              {timeSlotLines.length > 0
                ? `${timeSlotLines.length} time slot${timeSlotLines.length !== 1 ? 's' : ''}`
                : 'None added'}
            </Text>
          </Group>
          {timeSlotLines.length > 0 && (
            <Stack gap={2}>
              {timeSlotLines.map(line => (
                <Text key={line} size="xs" c="dimmed">
                  {line}
                </Text>
              ))}
            </Stack>
          )}
        </Stack>
      </Box>

      {/* What happens next */}
      <Alert icon={<Info size={16} />} color="blue" variant="light">
        <Text size="sm" fw={600} mb="xs">What happens next?</Text>
        <Group gap="xs">
          <Badge size="xs" color="gray">Draft</Badge>
          <Text size="sm">Cohort created in Draft status</Text>
        </Group>
      </Alert>
    </Stack>
  );
}
